import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Settings as SettingsIcon, Building2, FileText, Users as UsersIcon,
  User as UserIcon, Mail, MessageSquare, Facebook, ShieldCheck, Webhook, Headset,
} from "lucide-react";
import { AccountCard, BusinessSection, FiscalSection, EmailSection } from "@/pages/salon/Settings";
import { MembersTab } from "@/pages/salon/MembersTab";
import WhatsAppSection from "@/pages/salon/WhatsAppSection";
import MetaPagesSection from "@/pages/salon/MetaPagesSection";
import AgencyUsers from "@/pages/admin/AgencyUsers";
import AgencyLeadWebhooks from "@/pages/admin/AgencyLeadWebhooks";
import { AcquisitionChannelSection } from "@/pages/admin/agency/AcquisitionChannelSection";

// Sezioni sidebar delle Impostazioni Agenzia. Riusa gli stessi componenti del
// tenant per garantire look & feel identico. Le sezioni tenant che non hanno
// senso per l'HQ (orari, servizi, magazzino, tags…) non sono incluse.
const SECTIONS = [
  { key: "account", label: "Il mio account", icon: UserIcon },
  { key: "business", label: "Anagrafica", icon: Building2 },
  { key: "fiscal", label: "Dati fiscali", icon: FileText },
  { key: "email", label: "Email mittente", icon: Mail },
  { key: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { key: "acquisition", label: "Canale acquisizione", icon: Headset },
  { key: "meta", label: "Facebook & IG", icon: Facebook },
  { key: "access", label: "Collaboratori", icon: UsersIcon },
  { key: "lead_webhooks", label: "Lead Webhooks", icon: Webhook },
  { key: "accounts", label: "Account Memento", icon: ShieldCheck },
];

export default function AgencySettings() {
  const { user } = useAuth() || {};
  const [section, setSection] = useState("account");
  const [info, setInfo] = useState({ name: "", address: "", phone: "", email: "" });
  const [fiscal, setFiscal] = useState({
    legal_name: "", vat_number: "", tax_code: "", sdi_code: "", pec_email: "",
    legal_address: "", regime: "", ateco_code: "", inps_gestione: "",
  });
  const [emailCfg, setEmailCfg] = useState({ sender_name: "", reply_to_email: "" });

  const load = () => {
    api.get("/tenant").then((r) => {
      const t = r.data || {};
      setInfo({
        name: t.name || "", address: t.address || "",
        phone: t.phone || "", email: t.email || "",
      });
      setFiscal({
        legal_name: t.legal_name || "", vat_number: t.vat_number || "",
        tax_code: t.tax_code || "", sdi_code: t.sdi_code || "",
        pec_email: t.pec_email || "", legal_address: t.legal_address || "",
        regime: t.regime || "", ateco_code: t.ateco_code || "",
        inps_gestione: t.inps_gestione || "",
      });
      setEmailCfg({
        sender_name: t.sender_name || "",
        reply_to_email: t.reply_to_email || "",
      });
    });
  };
  useEffect(() => { load(); }, []);

  const saveInfo = async (e) => {
    e.preventDefault();
    try { await api.patch("/tenant", { ...info, email: info.email || null }); toast.success("Anagrafica salvata"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };
  const saveFiscal = async (e) => {
    e.preventDefault();
    try { await api.patch("/tenant", fiscal); toast.success("Dati fiscali salvati"); load(); }
    catch { toast.error("Errore"); }
  };
  const saveEmailCfg = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/tenant", { sender_name: emailCfg.sender_name || null, reply_to_email: emailCfg.reply_to_email || null });
      toast.success("Impostazioni email salvate"); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  return (
    <div data-testid="agency-settings-page" className="space-y-6">
      <header>
        <h1 className="font-display text-4xl font-black tracking-tight inline-flex items-center gap-3">
          <SettingsIcon className="h-7 w-7 text-[color:var(--sp-brand)]" />
          Impostazioni agenzia
        </h1>
        <p className="text-[color:var(--sp-text-secondary)] mt-1">Configurazione Salon Up HQ: anagrafica, fatturazione, integrazioni social e collaboratori.</p>
      </header>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Sub-nav sinistra — stesso stile del tenant */}
        <aside className="sp-card p-2 h-fit sticky top-4">
          <nav className="space-y-0.5" data-testid="settings-subnav">
            {SECTIONS.map((s) => {
              const Icon = s.icon;
              const active = section === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSection(s.key)}
                  data-testid={`settings-tab-${s.key}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium text-left transition-colors ${
                    active ? "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" : "text-[color:var(--sp-text-secondary)] hover:bg-[color:var(--sp-subtle)]"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {s.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Contenuto sezione */}
        <div className="min-w-0">
          {section === "account" && <AccountCard user={user} />}
          {section === "business" && <BusinessSection info={info} setInfo={setInfo} onSave={saveInfo} />}
          {section === "fiscal" && <FiscalSection fiscal={fiscal} setFiscal={setFiscal} onSave={saveFiscal} />}
          {section === "email" && <EmailSection emailCfg={emailCfg} setEmailCfg={setEmailCfg} onSave={saveEmailCfg} salonName={info.name} />}
          {section === "whatsapp" && <WhatsAppSection />}
          {section === "acquisition" && <AcquisitionChannelSection />}
          {section === "meta" && <MetaPagesSection />}
          {section === "access" && <MembersTab />}
          {section === "lead_webhooks" && <AgencyLeadWebhooks />}
          {section === "accounts" && <AgencyUsers />}
        </div>
      </div>
    </div>
  );
}
